import {Badge, Button, Divider, Paper, Stack, Text, UnstyledButton} from '@mantine/core'
import {useNavigate} from '@tanstack/react-router'
import {format} from 'date-fns'
import type {MouseEvent, ReactNode} from 'react'

import {toLocalDate} from '../jobs/job-schedule-format'
import {ProjectBillingRail} from './project-billing-rail'
import classes from './project-list-card.module.css'
import type {ProjectBillingMethod, ProjectLifecycleStatus, ProjectListItem} from './schemas'

type ProjectListCardProps = {
    project: ProjectListItem
}

export function ProjectListCard({project}: ProjectListCardProps) {
    const navigate = useNavigate()

    const openJobCount = project.plannedCount + project.activeCount
    // An order whose every job was canceled is not a live estimate
    const allCanceled = project.jobCount > 0 && project.cancelledCount === project.jobCount
    const jobSummaryLabel =
        project.jobCount > 0
            ? openJobCount > 0
                ? `${openJobCount} of ${project.jobCount} jobs open`
                : allCanceled
                  ? `${project.jobCount} of ${project.jobCount} jobs canceled`
                  : `${project.jobCount} of ${project.jobCount} jobs closed`
            : 'No jobs yet'

    const scheduleLabel =
        project.earliestStart != null
            ? project.latestEnd != null
                ? `${format(toLocalDate(project.earliestStart), 'MMM d')} – ${format(toLocalDate(project.latestEnd), 'MMM d, yyyy')}`
                : format(toLocalDate(project.earliestStart), 'MMM d, yyyy')
            : null

    const billingMethodLabel =
        project.billingMethod != null
            ? formatBillingMethod(project.billingMethod)
            : 'Billing terms not set'

    const isClosedOrArchived = project.status === 'closed' || project.archivedAt != null

    // One metadata line — jobs · schedule · billing method.
    // The billing method drops on phones so the line still fits without wrapping.
    const metaParts: Array<{key: string; node: ReactNode; className?: string}> = [
        {
            key: 'jobs',
            className: project.jobCount > 0 ? undefined : classes['metaMuted'],
            node: (
                <>
                    {project.jobCount > 0 && (
                        <span className={classes['statusDot']} data-open={openJobCount > 0} />
                    )}
                    {jobSummaryLabel}
                </>
            ),
        },
        ...(scheduleLabel != null ? [{key: 'schedule', node: scheduleLabel}] : []),
        {
            key: 'billing',
            className: [
                classes['metaBilling'],
                project.billingMethod != null ? undefined : classes['metaMuted'],
            ]
                .filter(Boolean)
                .join(' '),
            node: billingMethodLabel,
        },
    ]

    function handleViewProject() {
        void navigate({
            to: '/projects/$projectId',
            params: {projectId: project.id},
        })
    }

    function handleAddJob(event: MouseEvent<HTMLButtonElement>) {
        event.stopPropagation()
        void navigate({
            to: '/projects/$projectId/jobs/add',
            params: {projectId: project.id},
            search: {},
        })
    }

    return (
        <Paper withBorder radius="md" className={classes['card']}>
            <UnstyledButton
                onClick={handleViewProject}
                className={classes['cardLink']}
                aria-label={`View order ${project.name}`}
            >
                {/* A plain element, not `Group`: Mantine drives wrapping through an
                    inline `--group-wrap`, which outranks the breakpoint rule below. */}
                <div className={classes['cardBody']}>
                    <Stack gap={0} className={classes['opsRegion']}>
                        <Stack gap={2} mb={10} style={{minWidth: 0}}>
                            <Text className={classes['projectName']} truncate>
                                {project.name}
                            </Text>
                            <Text size="sm" c="dimmed" truncate>
                                {project.customerName}
                            </Text>
                        </Stack>

                        <div className={classes['metaLine']}>
                            {/* The status pill leads the metadata line and takes no
                                separator after it, so the line reads as "state, then facts". */}
                            <Badge
                                variant="light"
                                tt="none"
                                className={classes['pill']}
                                styles={{
                                    root: allCanceled
                                        ? CANCELED_STYLE
                                        : {
                                              backgroundColor: STATUS_STYLE[project.status].bg,
                                              color: STATUS_STYLE[project.status].color,
                                          },
                                }}
                            >
                                {allCanceled ? 'Canceled' : STATUS_LABEL[project.status]}
                            </Badge>
                            {metaParts.map((part, index) => (
                                // The separator lives inside the item so hiding an item
                                // below the breakpoint takes its separator with it.
                                <span
                                    key={part.key}
                                    className={`${classes['metaItem']} ${part.className ?? ''}`}
                                >
                                    {index > 0 && (
                                        <span className={classes['metaSeparator']}>&middot;</span>
                                    )}
                                    {part.node}
                                </span>
                            ))}
                        </div>
                    </Stack>

                    <Divider orientation="vertical" className={classes['railDivider']} />
                    <ProjectBillingRail project={project} />
                </div>
            </UnstyledButton>

            {project.jobCount === 0 && !isClosedOrArchived && (
                <div className={classes['cardFooter']}>
                    <Button variant="subtle" color="brand" size="compact-sm" onClick={handleAddJob}>
                        Add a job to this order
                    </Button>
                </div>
            )}
        </Paper>
    )
}

function formatBillingMethod(method: ProjectBillingMethod): string {
    switch (method) {
        case 'line_rates':
            return 'Each line at its own rate'
        case 'hourly_transportation':
            return 'All-in hourly'
        case 'flat_rate':
            return 'Flat rate'
        case 'per_load':
            return 'Per load'
    }
}

const STATUS_LABEL: Record<ProjectLifecycleStatus, string> = {
    estimate: 'Estimate',
    in_progress: 'Active',
    closed: 'Closed',
}

const CANCELED_STYLE = {backgroundColor: '#fff5f5', color: '#c92a2a'}

const STATUS_STYLE: Record<ProjectLifecycleStatus, {bg: string; color: string}> = {
    estimate: {bg: '#f1f3f5', color: '#495057'},
    in_progress: {bg: '#ebfbee', color: '#2b8a3e'},
    closed: {bg: '#f1f3f5', color: '#495057'},
}
